import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollContainer, getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * Requests in flight by page number, so the prefetch and a render that needs the same page share
   * one request instead of asking for it twice
   * @type {Map<number, Promise<void>>}
   */
  #pageRequests = new Map();

  /** One render per direction at a time: a second trigger would read the cards before the first one
   * lands and append the same page again */
  #renderingNext = false;
  #renderingPrevious = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);

    window.addEventListener('pagehide', this.#saveScrollAnchor);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#restoreScrollAnchor();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    window.removeEventListener('pagehide', this.#saveScrollAnchor);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * Top of the scroll container in the viewport: 0 for the document, the box top for .page-wrapper
   * @returns {number}
   */
  #scrollContainerTop() {
    const container = getScrollContainer();
    return container === document.scrollingElement ? 0 : container.getBoundingClientRect().top;
  }

  /**
   * Back from a product, a reload (no bfcache) draws only the page of the URL, and the saved scrollTop
   * (scroll-container.js on desktop, the browser on mobile) belongs to the whole list: it lands elsewhere.
   * So the history entry also keeps the first card in view and its offset, to come back to it.
   *
   * La entrada además pasa a la página de esa tarjeta (?page=), así la vuelta carga directo la página
   * donde está y no hay que pedir las del medio una por una: antes se veía otra lista 5-10 s (QA
   * bb-plp-grilla-28). Y queda en 'manual' también en mobile, donde el navegador restauraba el scrollY
   * de la lista entera sobre una sola página y la tarjeta quedaba 1342 px corrida (bb-plp-grilla-29).
   */
  #saveScrollAnchor = () => {
    const { cards } = this.refs;
    if (!Array.isArray(cards)) return;

    const containerTop = this.#scrollContainerTop();
    const card = cards.find((item) => item.id && !item.hidden && item.getBoundingClientRect().bottom > containerTop);
    if (!card) return;

    try {
      const state = typeof history.state === 'object' && history.state !== null ? history.state : {};
      const url = new URL(window.location.href);
      if (card.dataset.page) url.searchParams.set('page', card.dataset.page);
      history.replaceState(
        {
          ...state,
          jjScrollAnchor: { id: card.id, page: card.dataset.page, top: card.getBoundingClientRect().top - containerTop },
        },
        '',
        url.toString()
      );
      history.scrollRestoration = 'manual';
    } catch (_) {
      // replaceState can throw if the state object exceeds the browser's size limit
    }
  };

  /**
   * Mobile: la entrada vuelve al scroll nativo ('auto', como la deja scroll-container.js) cuando la
   * página terminó de cargar, así el navegador ya no intenta restaurar encima del ancla. Desktop sigue
   * en 'manual' (.page-wrapper).
   */
  #releaseScrollRestoration() {
    const release = () => {
      if (!window.matchMedia('(min-width: 990px)').matches) history.scrollRestoration = 'auto';
    };
    if (document.readyState === 'complete') release();
    else window.addEventListener('load', release, { once: true });
  }

  /**
   * Volviendo del bfcache la lista está entera y en su lugar: sólo se suelta el 'manual' de mobile
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) this.#releaseScrollRestoration();
  };

  /**
   * Only where the theme restores the scroll by hand: desktop (.page-wrapper) and, since the anchor
   * was saved, also mobile (#saveScrollAnchor). The URL is already the card's page; if it isn't (an
   * entry saved before), the pages between the URL's and the card's are loaded until the card is back
   * (earlier ones prepended, later ones appended), then it goes to its offset and stays there.
   */
  async #restoreScrollAnchor() {
    const anchor = history.state?.jjScrollAnchor;
    if (!anchor || history.scrollRestoration !== 'manual') return;

    // The anchor replaces scroll-container.js's scrollTop restore, which doesn't know the list
    const { scrollTop, ...state } = history.state;
    history.replaceState(state, '');
    this.#releaseScrollRestoration();

    const { cards } = this.refs;
    const lastCard = Array.isArray(cards) ? cards[cards.length - 1] : undefined;
    const forward = Number(anchor.page) > Number(lastCard?.dataset.page);

    // The infinite scroll may be loading already (its marker starts in view): wait for it
    const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));
    let card = document.getElementById(anchor.id);
    for (let tries = 0; !card && tries < 300; tries++) {
      if (forward ? this.#renderingNext : this.#renderingPrevious) {
        await nextFrame();
      } else if (!(await (forward ? this.#renderNextPage() : this.#renderPreviousPage()))) {
        break;
      }
      card = document.getElementById(anchor.id);
    }
    if (!card) return;

    await nextFrame();
    this.#pinScrollAnchor(anchor);
  }

  /**
   * Puts the anchor card at its offset and keeps it there while the list still grows above it (a page
   * the infinite scroll prepends after the restore), until the shopper scrolls or 3 s go by.
   * @param {{ id: string, top: number }} anchor
   */
  #pinScrollAnchor(anchor) {
    const pin = () => {
      const card = document.getElementById(anchor.id);
      if (!card) return;
      const offset = card.getBoundingClientRect().top - this.#scrollContainerTop() - anchor.top;
      if (Math.abs(offset) >= 1) scrollTo({ top: getScrollTop() + offset, behavior: 'instant' });
    };
    pin();

    const { grid } = this.refs;
    if (!grid) return;
    const userEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    const observer = new ResizeObserver(pin);
    const stop = () => {
      observer.disconnect();
      window.clearTimeout(timer);
      for (const type of userEvents) window.removeEventListener(type, stop, true);
    };
    const timer = window.setTimeout(stop, 3000);
    for (const type of userEvents) window.addEventListener(type, stop, { capture: true, passive: true });
    observer.observe(grid);
  }

  /**
   * The refs follow the DOM through a MutationObserver that Component only starts on idle: a page
   * appended or prepended while the page is still loading (the restore above, a fast scroll) left
   * `cards` stale, so the next page came out the same one again and was added once more, every card
   * hidden as repeated. They are read again right after each change.
   */
  #syncRefs() {
    this.updatedCallback();
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    // A failed request also resolves it, so the render waiting for it doesn't keep its lock
    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo) || this.pages.has(pageNumber)) return;

    const inFlight = this.#pageRequests.get(pageNumber);
    if (inFlight) return inFlight;

    const request = sectionRenderer
      .getSectionHTML(this.sectionId, true, pageInfo.url)
      .then((pageContent) => {
        this.pages.set(pageNumber, pageContent);
      })
      .finally(() => {
        if (this.#pageRequests.get(pageNumber) === request) this.#pageRequests.delete(pageNumber);
      });
    this.#pageRequests.set(pageNumber, request);
    return request;
  }

  /**
   * @returns {Promise<boolean>} Whether a page was appended
   */
  async #renderNextPage() {
    if (this.#renderingNext) return false;
    this.#renderingNext = true;

    let appended = false;
    try {
      appended = await this.#appendNextPage();
    } finally {
      this.#renderingNext = false;
    }

    if (appended) this.#reobserve(this.refs.viewMoreNext);
    return appended;
  }

  /**
   * @returns {Promise<boolean>} Whether a page was prepended
   */
  async #renderPreviousPage() {
    if (this.#renderingPrevious) return false;
    this.#renderingPrevious = true;

    let prepended = false;
    try {
      prepended = await this.#prependPreviousPage();
    } finally {
      this.#renderingPrevious = false;
    }

    if (prepended) this.#reobserve(this.refs.viewMorePrevious);
    return prepended;
  }

  /**
   * A trigger ignored while a render was running doesn't come back by itself if the marker is still
   * in view: observing it again reports its current state
   * @param {Element | undefined} viewMore
   */
  #reobserve(viewMore) {
    if (!viewMore || !this.infinityScrollObserver) return;
    this.infinityScrollObserver.unobserve(viewMore);
    this.infinityScrollObserver.observe(viewMore);
  }

  /**
   * @returns {Promise<boolean>} Whether a page was appended
   */
  async #appendNextPage() {
    const { grid } = this.refs;

    if (!grid) return false;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return false;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return false;
    }

    grid.append(...nextPageItemElements);
    this.#syncRefs();

    const repeated = nextPageItemElements.filter(
      (item) => item instanceof HTMLElement && item.dataset.repeated !== undefined
    ).length;
    if (repeated) {
      await this.#recoverSkippedProducts(nextPage.page, nextPage.url, repeated);
      this.#syncRefs();
    }

    this.#aspectRatioHelper.processNewElements();

    await yieldToMainThread();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });

    return true;
  }

  /**
   * @returns {Promise<boolean>} Whether a page was prepended
   */
  async #prependPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return false;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return false;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return false;
    }

    // Store the current scroll position and height of the first element. The first card that is
    // drawn: the grid's first child can be its inline <script> or a hidden repeated card, which have no
    // box (top 0), and then the prepended page pushed the view down by its whole height
    const currentScrollTop = getScrollTop();
    const firstElement = Array.from(grid.children).find(
      (element) => element instanceof HTMLElement && element.getClientRects().length > 0
    );
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);
    this.#syncRefs();

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    const items = Array.from(gridElement.querySelectorAll(':scope > [ref="cards[]"]'));

    // The relevance order of search is not stable between pages, so a product can come again in the
    // next one: it stays hidden and without id, only as the marker of its page
    for (const item of items) {
      if (item instanceof HTMLElement && item.id && document.getElementById(item.id)) {
        item.hidden = true;
        item.removeAttribute('id');
        item.dataset.repeated = '';
      }
    }

    return items;
  }

  /**
   * Each product that came again took the place of another one, which that order left out of both
   * pages. Ask again for this page and the previous one, without cache (the order changes between
   * requests), and add the products that are not in the grid yet as cards of this page
   * @param {number} page
   * @param {URL} pageUrl
   * @param {number} missing
   */
  async #recoverSkippedProducts(page, pageUrl, missing) {
    const { grid } = this.refs;
    if (!grid) return;

    for (const pageNumber of [page, page - 1]) {
      if (missing <= 0 || pageNumber < 1) break;

      const url = new URL(pageUrl);
      url.searchParams.set('page', pageNumber.toString());

      let pageHTML;
      try {
        pageHTML = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
      } catch {
        return;
      }

      const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
      const items = parsedPage.querySelectorAll('[ref="grid"] > [ref="cards[]"]');

      for (const item of items) {
        if (missing <= 0) break;
        if (!(item instanceof HTMLElement) || !item.id || document.getElementById(item.id)) continue;

        item.dataset.page = page.toString();
        const pageCards = grid.querySelectorAll(`:scope > [data-page="${page}"]`);
        const lastPageCard = pageCards[pageCards.length - 1];
        if (lastPageCard) {
          lastPageCard.after(item);
        } else {
          grid.append(item);
        }
        missing--;
      }
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();
    this.#pageRequests.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
